<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-nodeItem-actions-renameNode-activator">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-renameNode-modal">
      <v-card-title class="d-flex justify-space-between">
        {{ dialogTitle }}

        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-form ref="form" @submit.prevent="formSubmit()">
        <v-card-text class="pb-0">
          <v-text-field
            v-model="nodeName"
            :rules="nodeNameRules"
            :placeholder="$t('routes.node.renameNodeDialogFoldername')"
            density="compact"
            variant="solo-filled"
            required
            flat
            data-cy="dialog-renameNode-nodeName"
          />

          <div v-if="nodeNameError" class="bg-error">
            {{ nodeNameError }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            type="submit"
            color="primary"
            variant="flat"
            elevation="0"
            :disabled="isSubmitting"
            data-cy="dialog-renameNode-ok"
          >
            {{ $t('common.rename') }}
          </v-btn>
          <v-btn
            variant="text"
            @click="dialog = false"
            data-cy="dialog-renameNode-cancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { useNodeStore } from '@/stores/NodeStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import {useCommonStore} from '@/stores/CommonStore.js';

export default {
  mixins: [DialogActionKeys],
  name: "RenameNodeDialog",
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    node: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      nodeName: this.node.name || "",
      nodeNameRules: [
        (v) => !!v || this.$t('routes.node.renameNodeDialogInvalidName'),
        (v) => v && v.length <= 191 || this.$t('routes.node.folderNameTooLong'),
      ],
      nodeNameError: "",
      isSubmitting: false,
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.nodeName = this.node.name;
        this.nodeNameError = "";
        // Prevent double submission during dialog closure
        this.isSubmitting = false;
      }
    },
  },
  methods: {
    async formSubmit() {
      if (this.isSubmitting) {
        return;
      }

      const validForm = await this.$refs.form.validate();
      this.nodeNameError = "";

      if (!validForm.valid) {
        return;
      }

      const trimmedName = this.nodeName.trim();

      if (trimmedName === this.node.name) {
        this.dialog = false;
        return;
      }

      const payload = {
        id: this.node.id,
        name: trimmedName,
      };

      const nodeStore = useNodeStore();
      this.isSubmitting = true;
      try {
        await nodeStore.renameNode(payload);
        this.dialog = false;
        this.$showMessage(this.$t('routes.node.folderRenamed'), { type: 'success' });
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.nodeNameError = error.response.data.error.message;
        } else {
          this.nodeNameError = "Error!";
          console.log(error);
        }
        this.isSubmitting = false;
      }
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    nodeTypes() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    dialogTitle() {
      if (this.node && this.node.type === this.nodeTypes.FOLDER) {
        return this.$t('routes.node.renameFolder');
      }

      if (this.node && this.node.type === this.nodeTypes.CLIENT) {
        return this.$t('routes.node.renameClient');
      }

      if (this.node && this.node.type === this.nodeTypes.PROJECT) {
        return this.$t('routes.node.renameProject');
      }

      return this.$t('common.rename');
    }
  },
};
</script>

<style>

</style>
